import { useEffect, useMemo, useRef, useState } from 'react'
import { View, Platform, TextInput as RNTextInput } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import * as Linking from 'expo-linking'
import { useTranslation } from 'react-i18next'
import { useAuthRhythm } from '../../lib/hooks/use-auth-rhythm'
import { AuthGradientButton } from '../../components/auth/AuthGradientButton'
import { AuthScaffold } from '../../components/auth/AuthScaffold'
import { impact, ImpactStyle, notification, NotificationType } from '@/lib/utils/haptics'
import Animated, { FadeInDown, FadeInUp, useReducedMotion } from 'react-native-reanimated'
import { Lock, ShieldCheck } from '@/components/ui/icons'
import { TextInput } from '../../components/ui/TextInput'
import { PasswordStrengthBar } from '../../components/auth/PasswordStrengthBar'
import { AuthErrorBanner } from '../../components/auth/AuthErrorBanner'
import { AuthFooterLink } from '../../components/auth/AuthFooterLink'
import { AuthHeader } from '../../components/auth/AuthHeader'
import { AuthSuccessPanel } from '../../components/auth/AuthSuccessPanel'
import { BackButton } from '../../components/ui/BackButton'
import { useBreakpoint } from '../../lib/hooks/use-breakpoint'
import { SPACING } from '../../lib/brand'
import { useAuth } from '../../lib/hooks/use-auth'
import { useAuthStore } from '../../lib/stores/auth.store'
import { resetPasswordSchema } from '../../lib/validations/auth.schema'
import { captureError, errorMessage } from '../../lib/utils/observability'
import { useSemanticColors, withAlpha, DURATION, STAGGER } from '../../lib/theme/tokens'
import { useTheme } from '../../lib/hooks/use-theme'
import { supabase } from '../../lib/supabase'
import {
  createPasswordRecoverySessionCoordinator,
  isInvalidPasswordRecoveryError,
  isInvalidPasswordRecoverySessionError,
  mergeRecoveryParams,
  parseRecoveryParamsFromUrl,
  type RecoveryLinkParams,
} from '../../lib/auth/password-recovery-session'

// One owner across screen remounts: an email token can be consumed only once.
const recoverySession = createPasswordRecoverySessionCoordinator(supabase.auth, {
  storage: {
    get: () => {
      if (Platform.OS !== 'web' || typeof window === 'undefined') return null
      const proof = window.sessionStorage.getItem('tago.auth.password-recovery-proof')
      return proof ? JSON.parse(proof) : null
    },
    set: (proof) => {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.sessionStorage.setItem('tago.auth.password-recovery-proof', JSON.stringify(proof))
      }
    },
    clear: () => {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.sessionStorage.removeItem('tago.auth.password-recovery-proof')
      }
    },
  },
})
type SearchParamValue = string | string[] | undefined

function firstParam(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value
}

export default function ResetPasswordScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const rawParams = useLocalSearchParams()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { isDesktop } = useBreakpoint()
  const { updatePassword, updatePasswordPending, updatePasswordError, signOut } = useAuth()
  const setCompletingSignup = useAuthStore((s) => s.setCompletingSignup)
  const reducedMotion = useReducedMotion()

  const iconColor = colors.textMuted

  const confirmRef = useRef<RNTextInput>(null)

  const [form, setForm] = useState({ password: '', confirmPassword: '' })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [recoveryStatus, setRecoveryStatus] = useState<'checking' | 'ready' | 'invalid' | 'retry'>(
    'checking',
  )
  const [recoveryError, setRecoveryError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [recoveryRetry, setRecoveryRetry] = useState(0)
  const [isLeaving, setIsLeaving] = useState(false)
  const cleanupPromise = useRef<Promise<void> | null>(null)
  const passwordUpdateInFlight = useRef(false)
  const translateRef = useRef(t)
  translateRef.current = t

  const sp = useAuthRhythm()

  const routeRecoveryParams = useMemo<RecoveryLinkParams>(
    () => ({
      code: firstParam(rawParams.code as SearchParamValue),
      tokenHash:
        firstParam(rawParams.token_hash as SearchParamValue) ??
        firstParam(rawParams.tokenHash as SearchParamValue),
      accessToken:
        firstParam(rawParams.access_token as SearchParamValue) ??
        firstParam(rawParams.accessToken as SearchParamValue),
      refreshToken:
        firstParam(rawParams.refresh_token as SearchParamValue) ??
        firstParam(rawParams.refreshToken as SearchParamValue),
      errorCode:
        firstParam(rawParams.error_code as SearchParamValue) ??
        firstParam(rawParams.error as SearchParamValue),
    }),
    [
      rawParams.access_token,
      rawParams.accessToken,
      rawParams.code,
      rawParams.error_code,
      rawParams.error,
      rawParams.refresh_token,
      rawParams.refreshToken,
      rawParams.token_hash,
      rawParams.tokenHash,
    ],
  )

  useEffect(() => {
    let cancelled = false

    const establishRecoverySession = async () => {
      setCompletingSignup(true)
      setRecoveryStatus('checking')
      setRecoveryError(null)

      try {
        const initialUrl = await Linking.getInitialURL().catch(() => null)
        let recoveryParams = mergeRecoveryParams(
          routeRecoveryParams,
          parseRecoveryParamsFromUrl(initialUrl),
        )

        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          recoveryParams = mergeRecoveryParams(
            parseRecoveryParamsFromUrl(window.location.href),
            recoveryParams,
          )
        }
        await recoverySession.establish(recoveryParams)
        if (cancelled) return

        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const url = new URL(window.location.href)
          url.searchParams.delete('code')
          url.searchParams.delete('token_hash')
          url.searchParams.delete('tokenHash')
          url.searchParams.delete('access_token')
          url.searchParams.delete('accessToken')
          url.searchParams.delete('refresh_token')
          url.searchParams.delete('refreshToken')
          url.hash = ''
          window.history.replaceState({}, document.title, `${url.pathname}${url.search}`)
        }

        setRecoveryStatus('ready')
      } catch (err) {
        if (cancelled) return
        const invalidLink = isInvalidPasswordRecoveryError(err)
        setRecoveryStatus(invalidLink ? 'invalid' : 'retry')
        setRecoveryError(
          invalidLink
            ? translateRef.current('auth.errors.reset_link_expired')
            : errorMessage(err, translateRef.current('auth.errors.network')),
        )
        void notification(NotificationType.Error)
        captureError(err, { screen: 'reset-password', action: 'establish-recovery-session' })
      }
    }

    void establishRecoverySession()

    return () => {
      cancelled = true
    }
  }, [routeRecoveryParams, recoveryRetry, setCompletingSignup])

  const updateForm = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => ({ ...prev, [key]: '' }))
  }

  const releaseRecoverySession = () => {
    if (!cleanupPromise.current) {
      cleanupPromise.current = recoverySession
        .clear()
        .then((ownsSession) => (ownsSession ? signOut() : undefined))
        .catch((err: unknown) => {
          cleanupPromise.current = null
          captureError(err, { screen: 'reset-password', action: 'release-recovery-session' })
          throw err
        })
    }
    return cleanupPromise.current
  }

  const handleReset = async () => {
    impact(ImpactStyle.Light)

    if (recoveryStatus !== 'ready' || isLeaving || passwordUpdateInFlight.current) {
      return
    }

    const result = resetPasswordSchema.safeParse(form)
    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      for (const issue of result.error.issues) {
        const key = issue.path[0] as string
        fieldErrors[key] = t(issue.message)
      }
      setErrors(fieldErrors)
      void notification(NotificationType.Error)
      return
    }
    setErrors({})
    setRecoveryError(null)
    passwordUpdateInFlight.current = true

    try {
      // The session can change in another tab while the form is still ready.
      // Recheck the account proven by this recovery grant before changing it.
      await recoverySession.establish({})
      await updatePassword(result.data.password)
    } catch (err) {
      // Surface the real ServiceError reason
      // (e.g., "weak password" / "session expired") + capture to Sentry so
      // we see why password resets are failing in production.
      void notification(NotificationType.Error)
      const sessionInvalid = isInvalidPasswordRecoverySessionError(err)
      const message = sessionInvalid
        ? t('auth.errors.reset_link_expired')
        : errorMessage(err, t('auth.errors.reset_failed'))
      setRecoveryError(message)
      if (sessionInvalid) setRecoveryStatus('invalid')
      captureError(err, { screen: 'reset-password', action: 'update-password' })
      return
    } finally {
      passwordUpdateInFlight.current = false
    }

    // Clear password from memory immediately
    setForm({ password: '', confirmPassword: '' })

    // Show success screen before cleaning up session
    setSuccess(true)
    void notification(NotificationType.Success)

    // Sign out the recovery session — user will sign in fresh with new password.
    // This also clears isCompletingSignup via resetStore in the SIGNED_OUT handler.
    void releaseRecoverySession().then(
      () => setCompletingSignup(false),
      (err: unknown) => setRecoveryError(errorMessage(err, t('auth.errors.network'))),
    )
  }

  // The button fires its own press-in haptic; the explicit `impact` that used
  // to live here double-tapped the taptic engine.
  const handleSignIn = () => {
    void leaveRecovery('/(auth)/login')
  }

  /**
   * Every way off this screen has to drop the recovery session first, or the
   * next screen opens signed in as a half-authenticated recovery user.
   */
  const leaveRecovery = async (href: '/(auth)/login' | '/(auth)/forgot-password') => {
    if (isLeaving) return
    setIsLeaving(true)
    try {
      await releaseRecoverySession()
      setCompletingSignup(false)
      router.replace(href)
    } catch (err) {
      setRecoveryError(errorMessage(err, t('auth.errors.network')))
    } finally {
      setIsLeaving(false)
    }
  }

  const handleRequestNewLink = () => leaveRecovery('/(auth)/forgot-password')
  const handleLeaveForLogin = () => leaveRecovery('/(auth)/login')

  // An expired link cannot be rescued by anything typed into this form, so the
  // primary action stops being "Restablecer Contraseña" — a permanently
  // disabled 40%-opacity gradient with white text on it — and becomes the one
  // action that does work.
  const linkExpired = recoveryStatus === 'invalid'

  const bodyStyle = isDesktop ? undefined : { flexGrow: 1, justifyContent: 'center' as const }

  // ============================
  //  SUCCESS STATE
  // ============================
  if (success) {
    return (
      <AuthScaffold
        brandHeadline={t('welcome.headline')}
        brandSubtitle={t('welcome.subheadline')}
        background={colors.background}
      >
        <AuthSuccessPanel
          icon={<ShieldCheck size={40} color={colors.success} />}
          // The disc used to be tinted with a hardcoded iOS green while the
          // glyph on it came from the success token — two different greens,
          // one on top of the other, in both themes.
          iconBackground={withAlpha(colors.success, isDark ? 0.15 : 0.1)}
          title={t('auth.reset_password_success_title')}
          description={t('auth.reset_password_success_desc')}
        >
          {/* Sign In — gradient CTA */}
          <View style={{ width: '100%' }}>
            <AuthErrorBanner error={recoveryError} />
            <AuthGradientButton
              title={t('auth.sign_in_button')}
              onPress={handleSignIn}
              loading={isLeaving}
              testID="reset-password.sign-in-button"
              entering={
                reducedMotion ? undefined : FadeInUp.duration(DURATION.normal).delay(STAGGER * 8)
              }
            />
          </View>
        </AuthSuccessPanel>
      </AuthScaffold>
    )
  }

  // ============================
  //  FORM CONTENT
  // ============================
  const formContent = (
    <>
      {/* ─── Navigation ─── The two screens either side of this one open with
          a back control; this one opened with its headline hard against the
          top edge and no way out but a 14pt text link. */}
      <BackButton
        testID="reset-password.back-button"
        onPress={handleLeaveForLogin}
        style={{ marginBottom: SPACING.sm }}
      />

      {/* ─── Body ─── grows into the empty bottom of the display instead of
          leaving the form stacked against the top. */}
      <View style={bodyStyle}>
        {/* ─── Headline + subtitle ─── once the link is dead this stops being
            "create a new password" — there is nothing here that could create
            one — and becomes the sibling screen's promise, which is exactly
            what the CTA below now does. The screen used to say "Creá una nueva
            contraseña." over two live fields, under a banner saying the link
            had expired, above a button labelled "Enviar Enlace". */}
        {linkExpired ? (
          <AuthHeader
            title={t('forgot_password.headline')}
            subtitle={t('forgot_password.subtitle')}
          />
        ) : (
          <AuthHeader
            title={t('reset_password.headline')}
            subtitle={t('reset_password.subtitle')}
          />
        )}

        {/* ─── Password ─── the two fields and the strength meter only exist
            while a recovery session can still be established. With an expired
            link they are inert by construction (`handleReset` refuses before
            reading them), so leaving them on screen offered the user work that
            could not land. */}
        {linkExpired ? null : (
          <>
            <Animated.View
              collapsable={false}
              entering={
                reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER * 4)
              }
            >
              <TextInput
                label={t('auth.new_password')}
                placeholder={t('auth.password_placeholder')}
                value={form.password}
                onChangeText={(v) => updateForm('password', v)}
                secureTextEntry
                autoComplete="new-password"
                textContentType="newPassword"
                returnKeyType="next"
                blurOnSubmit={false}
                onSubmitEditing={() => confirmRef.current?.focus()}
                error={errors.password}
                leftIcon={<Lock size={18} color={iconColor} />}
                testID="reset-password.password-input"
              />
            </Animated.View>

            {/* ─── Password strength ─── */}
            <PasswordStrengthBar
              password={form.password}
              isDark={isDark}
              testID="reset-password.password-strength"
            />

            {/* ─── Confirm password ─── */}
            <Animated.View
              collapsable={false}
              entering={
                reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER * 4)
              }
            >
              <TextInput
                ref={confirmRef}
                label={t('auth.confirm_password')}
                placeholder={t('auth.password_placeholder')}
                value={form.confirmPassword}
                onChangeText={(v) => updateForm('confirmPassword', v)}
                secureTextEntry
                autoComplete="new-password"
                textContentType="newPassword"
                returnKeyType="go"
                onSubmitEditing={handleReset}
                error={errors.confirmPassword}
                leftIcon={<Lock size={18} color={iconColor} />}
                testID="reset-password.confirm-input"
              />
            </Animated.View>
          </>
        )}

        {/* ─── Action zone ─── */}
        <View style={{ marginTop: sp.formToAction }}>
          {/* Error banner */}
          <AuthErrorBanner
            error={
              recoveryError ??
              (updatePasswordError
                ? errorMessage(updatePasswordError, t('auth.errors.reset_failed'))
                : null)
            }
          />

          {/* CTA — carries the recovery-link check itself. The screen used to
              open on a standalone "Cargando…" pill above a dead button, which
              read as an error state on a screen that had not failed. The button
              is already disabled while checking, so the spinner belongs on it.
              Once the link is known to be expired the button stops being a
              dead end and becomes the request for a fresh link. */}
          {linkExpired ? (
            <AuthGradientButton
              title={t('auth.send_reset_link')}
              onPress={handleRequestNewLink}
              testID="reset-password.new-link-button"
              entering={
                reducedMotion ? undefined : FadeInUp.duration(DURATION.normal).delay(STAGGER * 5)
              }
            />
          ) : recoveryStatus === 'retry' ? (
            <AuthGradientButton
              title={t('common.retry')}
              onPress={() => setRecoveryRetry((attempt) => attempt + 1)}
              testID="reset-password.retry-button"
              loading={isLeaving}
            />
          ) : (
            <AuthGradientButton
              title={t('auth.reset_password_button')}
              onPress={handleReset}
              loading={updatePasswordPending || recoveryStatus === 'checking' || isLeaving}
              disabled={recoveryStatus !== 'ready' || isLeaving}
              testID="reset-password.submit-button"
              entering={
                reducedMotion ? undefined : FadeInUp.duration(DURATION.normal).delay(STAGGER * 5)
              }
            />
          )}
        </View>

        {/* ─── Footer ─── the escape hatch the CTA is not offering: sign in
            when the link expired, a fresh link while the form is still live.
            The lone unprompted link this screen used to close with was the
            only footer in the flow without its question. */}
        {linkExpired ? (
          <AuthFooterLink
            prompt={t('auth.already_have_account')}
            linkLabel={t('auth.login')}
            onPress={handleLeaveForLogin}
            accessibilityLabel={`${t('auth.already_have_account')} ${t('auth.login')}`}
            testID="reset-password.login-link"
          />
        ) : (
          <AuthFooterLink
            linkLabel={t('auth.send_reset_link')}
            onPress={handleRequestNewLink}
            accessibilityLabel={t('auth.send_reset_link')}
            testID="reset-password.new-link"
          />
        )}
      </View>
    </>
  )

  return (
    <AuthScaffold
      brandHeadline={t('welcome.headline')}
      brandSubtitle={t('welcome.subheadline')}
      background={colors.background}
    >
      {formContent}
    </AuthScaffold>
  )
}
